// Jobs page: one trigger plus one instruction, which names the Skill.
const jobs = {
  title: "Jobs",
  description: "Skills that run on their own, on a schedule or when a webhook is called.",
  newJob: "New job",
  empty: {
    title: "No jobs yet",
    description: "A job runs a Skill on its own: every weekday at 9:00, or whenever a system calls its webhook.",
    adminHint: "Create one here, or ask the AI in the chat.",
  },
  loadFailed: "The jobs could not be loaded.",
  noWorkspace: "Select a workspace to see its jobs.",
  tabs: {
    jobs: "Jobs",
    runs: "Runs",
  },
  // Job proposed from a Skill
  proposal: {
    title: "Building the job from the Skill",
    stages: {
      loading: "Loading the form",
      filling: "Filling in the job from the Skill",
      finishing: "Making the final adjustments",
    },
    reason: "AI proposal: {{reason}} Review before saving.",
    ready: "the proposal was built from the Skill.",
    failed: "The proposal could not be built. The Skill is already cited in the instruction; fill in the rest by hand.",
  },
  // References in the instruction with @
  mentions: {
    skill: "Skill",
    mind: "Mind",
    workspace: "Workspace",
    tool: "Tool",
    // What each kind brings, in the first step of the list
    kindHint: {
      skill: "Installed Skills",
      mind: "Mind blocks",
      workspace: "Name, Soul, website and members",
      tool: "Installed integrations",
    },
    workspaceFields: {
      name: "Workspace name",
      description: "Soul (description)",
      website: "Website",
      members: "Members",
    },
    empty: "Nothing found.",
    loading: "Loading…",
    unknown: "Not found in this workspace: the job will not be saved with this reference.",
  },
  // Run history across every job
  runs: {
    searchPlaceholder: "Search the summary or by job name",
    filter: {
      all: "All",
      running: "Running",
      completed: "Completed",
      failed: "Failed",
      job: "Job",
    },
    period: {
      "24h": "Last 24 hours",
      "7d": "Last 7 days",
      "30d": "Last 30 days",
      all: "All time",
    },
    columns: {
      job: "Job",
      status: "Status",
      trigger: "Trigger",
      started: "Started",
      duration: "Duration",
      summary: "Summary",
      writes: "Changes",
      pending: "Pending",
    },
    status: {
      running: "Running",
      waiting: "Waiting",
      completed: "Completed",
      failed: "Failed",
      cancelled: "Cancelled",
    },
    trigger: {
      schedule: "Schedule",
      webhook: "Webhook",
      manual: "Manual",
    },
    // Filter by how the run started
    triggerFilter: {
      all: "All triggers",
      schedule: "On a schedule",
      webhook: "By webhook",
      manual: "Manual",
    },
    empty: {
      title: "No runs yet",
      description: "When a published job fires, each run shows here, with the summary of what the agent did.",
    },
    noResults: "No runs match these filters.",
    loadFailed: "The runs could not be loaded.",
    loadMore: "Load more",
    loadingRun: "Loading the run…",
    runLoadFailed: "This run could not be loaded.",
    jobSettings: "Job settings",
    actionsTitle: "Actions",
    actionFailed: "failed",
    usage: "{{calls}} model calls · {{tokens}} tokens",
  },
  filter: {
    searchPlaceholder: "Search by name, instruction or Skill",
    clearSearch: "Clear search",
    all: "All",
    published: "Published",
    draft: "Drafts",
    trigger: "Trigger",
    schedule: "On a schedule",
    webhook: "Webhook",
    clear: "Clear filters",
    noResults: "No jobs match these filters.",
  },
  columns: {
    name: "Name",
    instruction: "Instruction",
    trigger: "Trigger",
    status: "Status",
    lastRun: "Last run",
    actions: "Actions",
  },
  status: {
    draft: "Draft",
    published: "Published",
  },
  lastRun: {
    never: "Never ran",
    openHistory: "See the runs of this job",
  },
  actions: {
    test: "Run now",
    publish: "Publish",
    edit: "Job settings",
    history: "History",
    delete: "Delete",
  },
  // Draft changes not live yet
  pending: {
    state: {
      not_run: "Changes not run",
      running: "Running the changes…",
      completed: "Changes ran successfully",
      failed: "The run of the changes failed",
    },
    titlePublished: "Unpublished changes",
    titleDraft: "Not published yet",
    neverPublished: "This job was never published: nothing runs on its own until you publish it.",
    changed: "What changed",
    fields: {
      instruction: "Instruction",
      trigger: "Trigger",
      timeout_minutes: "Time limit per run",
      max_writes: "Change limit per run",
    },
    live: "Live",
    draft: "Draft",
    lastManualRun: "Manual run of this version",
    notRunYet: "This version has not run yet.",
    viewResult: "See result",
  },
  delete: {
    confirmTitle: "Delete {{name}}?",
    confirmDescription: "The job stops running and its run history is deleted with it. This cannot be undone.",
    confirmDescriptionWebhook:
      "The job stops running, its run history is deleted with it and the webhook address stops accepting calls. This cannot be undone.",
    confirm: "Delete",
    deleting: "Deleting…",
    cancel: "Cancel",
    success: "{{name}} was deleted",
  },
  form: {
    createTitle: "New job",
    editTitle: "Job settings",
    description: "What the agent does and when it runs.",
    name: "Name",
    namePlaceholder: "Weekly team status",
    descriptionLabel: "Description",
    descriptionPlaceholder: "What this job does (optional)",
    instruction: "Instruction",
    instructionPlaceholder:
      "Follow @skill:status-report: summarize what moved in @tool:[Azure DevOps] since the last run, compare it with the targets in @mind:goals and send the summary to @workspace:members.",
    instructionHelp: "Type @ to cite a Skill, a Mind block, workspace data or a Tool.",
    trigger: "Trigger",
    triggerSchedule: "On a schedule",
    triggerWebhook: "When a webhook is called",
    webhookHelp: "The job runs when an external system calls its address. What it sends reaches the agent as supporting material.",
    frequency: "Frequency",
    presets: {
      everyMinutes: "Every few minutes",
      everyHours: "Every few hours",
      daily: "Every day",
      weekdays: "Weekdays",
      weekly: "Weekly",
      monthly: "Monthly",
      custom: "Advanced (cron)",
    },
    time: "Time",
    every: "Every",
    minutes: "{{count}} minutes",
    hours_one: "{{count}} hour",
    hours_other: "{{count}} hours",
    days: "Days of the week",
    monthDay: "Day of the month",
    lastDay: "Last day of the month",
    window: "Only within a time window",
    windowFrom: "From",
    windowTo: "Until",
    weekdaysOnly: "Only on weekdays",
    cron: "Cron expression",
    cronHelp: "Five fields: minute hour day-of-month month day-of-week. 0 or 7 = Sunday, L = last day of the month. Example: 0 9 * * 1-5",
    cronFields: {
      minute: "minute",
      hour: "hour",
      day: "day of the month",
      month: "month",
      weekday: "day of the week",
    },
    invalidCronField: "Invalid value in the {{field}} field",
    invalidWindow: "The end must be after the start",
    pickDays: "Choose at least one day",
    timezone: "Time zone",
    timezoneSearch: "Search time zone",
    timezoneEmpty: "No time zone found",
    nextRuns: "Next runs",
    noPreview: "Fix the schedule to see when it runs",
    advanced: "Limits",
    timeout: "Time limit per run (minutes)",
    timeoutHelp: "1 to 60. Default 15.",
    maxWrites: "Changes in external systems per run",
    maxWritesHelp: "0 to 200. Default 20.",
    cancel: "Cancel",
    test: "Run now",
    publish: "Publish",
    publishing: "Publishing…",
    required: "Required",
    invalidTime: "Use HH:MM",
    invalidCron: "Use five fields: minute hour day-of-month month day-of-week",
  },
  saved: {
    created: "Job saved as a draft",
    updated: "Job updated",
    nextStep: "Run it now to check the result, then publish to start running it.",
  },
  webhook: {
    url: "Webhook address",
    authType: "Authentication",
    basic: "User and password (Basic)",
    bearer: "Token (Bearer)",
    username: "User",
    password: "Password",
    token: "Token",
    tokenPlaceholder: "Leave empty for Prodgy to generate one",
    show: "Show",
    hide: "Hide",
    copy: "Copy",
    copied: "Copied",
    loading: "Loading the webhook…",
    handoffTitle: "Webhook ready",
    handoffDescription: "Configure these in the system that will call the webhook. The job only receives calls after it is published; you can see these again in Job settings.",
    basicHint: "The caller sends the Authorization: Basic header, with the user and password below.",
    bearerHint: "The caller sends the Authorization: Bearer header, with the token below.",
    done: "Done",
  },
  test: {
    title: "Manual run: {{name}}",
    description: "Runs the draft once, now, without publishing. What the agent does in the systems is real and stays in the history.",
    stages: {
      queued: "Queued",
      working: "Agent working",
      result: "Result",
      completed: "Completed",
      failed: "Failed",
    },
    showMore: "Show more",
    showLess: "Show less",
    pendingCount: "{{count}} pending",
    summary: "Summary",
    pending: "Left pending",
    actions: "{{count}} actions",
    writes: "{{count}} changes in external systems",
    noReport: "The agent returned no report.",
    close: "Close",
    publish: "Publish",
  },
  publish: {
    confirmTitle: "Publish {{name}}?",
    confirmDescription: "From now on the trigger fires on its own. You can still edit the draft and publish again later.",
    recommendNotRun: "This version has not run yet. We recommend running it now and checking the result before publishing.",
    recommendFailed: "The last run of this version failed. We recommend fixing it and running it again before publishing.",
    confirm: "Publish",
    publishing: "Publishing…",
    cancel: "Cancel",
    success: "Job published",
  },
  settings: {
    jobNotice: "This is a job: one trigger and one agent step, edited on the Jobs page. Adding steps here turns it into a flow, and it moves to the Flows page.",
  },
};

export default jobs;
